/**
 * Helpers for resolving which layout codes apply to a given page, used by the
 * SSR rendering in src/components/LayoutCodeSSR.tsx. There is no longer a
 * client-side counterpart — SSR is the only place layout codes are rendered.
 */

export type PageType = 'home' | 'section' | 'article';

export function detectPageType(pathname: string): PageType {
  if (pathname === '/' || pathname === '') return 'home';
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length >= 5) return 'article';
  if (segments.length >= 1) return 'section';
  return 'home';
}

export function resolvePageId(
  pathname: string,
  routePageIds: Record<string, string>
): string | null {
  if (routePageIds[pathname]) return routePageIds[pathname];
  const segments = pathname.split('/').filter(Boolean);
  for (let i = segments.length; i >= 1; i--) {
    const pattern =
      '/' +
      segments
        .slice(0, i - 1)
        .concat('{slug}')
        .join('/');
    if (routePageIds[pattern]) return routePageIds[pattern];
  }
  if (segments.length === 1 && routePageIds['/{slug}'])
    return routePageIds['/{slug}'];
  return null;
}

export function findSpecificPageMatch(
  specificPageCodes: any[],
  pathname: string,
  currentPageId: string | null
): any | null {
  if (!specificPageCodes || !Array.isArray(specificPageCodes)) return null;

  for (const entry of specificPageCodes) {
    if (!entry.pages || !Array.isArray(entry.pages)) continue;
    const matches = entry.pages.some((p: any) => {
      if (!p) return false;
      if (currentPageId && p.id && p.id === currentPageId) return true;
      if (p.name) {
        const slug = p.name.toLowerCase().replace(/\s+/g, '-');
        if (pathname === `/${slug}` || pathname.startsWith(`/${slug}/`))
          return true;
      }
      if (p.id && (pathname === `/${p.id}` || pathname.startsWith(`/${p.id}/`)))
        return true;
      return false;
    });
    if (matches) return entry;
  }
  return null;
}

export function getCodeStrings(
  code: Record<string, any>,
  pageType: PageType,
  isMobile: boolean,
  pathname: string = '/',
  currentPageId: string | null = null
): { headerCode: string; bodyCode: string } {
  const scope = code.scope;
  const useMobile = code.hasSeparateMobileCode && isMobile;

  if (code.hasSpecificPageCodes && code.specificPageCodes) {
    const match = findSpecificPageMatch(
      code.specificPageCodes,
      pathname,
      currentPageId
    );
    if (match) {
      return {
        headerCode:
          (useMobile
            ? match.mobileHeaderCode || match.headerCode
            : match.headerCode) || '',
        bodyCode:
          (useMobile
            ? match.mobileBodyCode || match.bodyCode
            : match.bodyCode) || '',
      };
    }
  }

  if (scope === 'all_pages') {
    return {
      headerCode: (useMobile ? code.mobileHeaderCode : code.headerCode) || '',
      bodyCode: (useMobile ? code.mobileBodyCode : code.bodyCode) || '',
    };
  }

  const prefix = useMobile ? 'mobile' : '';
  const pageKey =
    pageType === 'home'
      ? 'homepage'
      : pageType === 'section'
        ? 'sectionPage'
        : 'articlePage';

  const headerKey = useMobile
    ? `${prefix}${pageKey.charAt(0).toUpperCase() + pageKey.slice(1)}HeaderCode`
    : `${pageKey}HeaderCode`;
  const bodyKey = useMobile
    ? `${prefix}${pageKey.charAt(0).toUpperCase() + pageKey.slice(1)}BodyCode`
    : `${pageKey}BodyCode`;

  return {
    headerCode: code[headerKey] || '',
    bodyCode: code[bodyKey] || '',
  };
}

/**
 * Best-effort mobile detection from a User-Agent string.
 * Server uses this to pick the mobile variant during SSR — the client
 * still re-evaluates via window-width on hydration and re-injects on mismatch.
 */
export function isMobileFromUA(userAgent: string | null | undefined): boolean {
  if (!userAgent) return false;
  return /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    userAgent
  );
}

export interface ActiveLayoutCode {
  slug: string;
  headerCode: string;
  bodyCode: string;
}

/**
 * Filter + resolve the layout codes that should render on a given page.
 * Skips inactive codes and codes that produce no head/body content.
 */
export function getActiveLayoutCodes(
  allCodes: Record<string, any>,
  pathname: string,
  isMobile: boolean,
  routePageIds: Record<string, string>
): ActiveLayoutCode[] {
  const pageType = detectPageType(pathname);
  const currentPageId = resolvePageId(pathname, routePageIds);
  const out: ActiveLayoutCode[] = [];
  for (const [slug, code] of Object.entries(allCodes)) {
    if (code.isActive === false || code.isActive === 'false' || !code.isActive)
      continue;
    const { headerCode, bodyCode } = getCodeStrings(
      code,
      pageType,
      isMobile,
      pathname,
      currentPageId
    );
    if (!headerCode && !bodyCode) continue;
    out.push({ slug, headerCode, bodyCode });
  }
  return out;
}

export interface ScriptPart {
  src?: string;
  async?: boolean;
  defer?: boolean;
  crossOrigin?: string;
  type?: string;
  content?: string;
}

/**
 * Split a layout-code HTML string into (a) non-script HTML and (b) extracted
 * <script> blocks. Used by SSR rendering: scripts are emitted as JSX <script>
 * elements (so they execute), the non-script HTML is rendered via
 * dangerouslySetInnerHTML on a wrapper element.
 */
export function parseLayoutCodeHtml(html: string): {
  nonScriptHtml: string;
  scripts: ScriptPart[];
} {
  if (!html || !html.trim()) return { nonScriptHtml: '', scripts: [] };
  const scripts: ScriptPart[] = [];
  const scriptRegex = /<script\b([^>]*)>([\s\S]*?)<\/script>/gi;
  const nonScriptHtml = html.replace(
    scriptRegex,
    (_match, attrStr: string, content: string) => {
      scripts.push({ ...parseScriptAttributes(attrStr), content });
      return '';
    }
  );
  return { nonScriptHtml, scripts };
}

function parseScriptAttributes(attrStr: string): Omit<ScriptPart, 'content'> {
  const out: Omit<ScriptPart, 'content'> = {};
  const attrRegex = /(\w[\w-]*)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|(\S+)))?/g;
  let m: RegExpExecArray | null;
  while ((m = attrRegex.exec(attrStr)) !== null) {
    const name = m[1].toLowerCase();
    const value = m[2] ?? m[3] ?? m[4] ?? '';
    if (name === 'src') out.src = value;
    else if (name === 'async') out.async = true;
    else if (name === 'defer') out.defer = true;
    else if (name === 'crossorigin') out.crossOrigin = value || 'anonymous';
    else if (name === 'type') out.type = value;
  }
  return out;
}
